<script setup lang="ts">
import { computed } from "vue";
import { UiText, UiIcon, UiNavLink, useApi } from "@shopexperts/shared";
import { useQuery } from "@tanstack/vue-query";
import type { ChatMessage, ProjectSide } from "../types";
const props = defineProps<{ project: NonNullable<ChatMessage['project_card']>; side: ProjectSide; createdAt: number }>();
const api = useApi();
const detail = useQuery({
  queryKey: computed(() => ["v2projects", "detail", props.project.project_id]),
  queryFn: () => (props.side === "expert" ? api.expert.projectsV2 : api.client.projectsV2).getProject({ params: { projectId: props.project.project_id } }),
  staleTime: 10_000,
});
const title = computed(() => detail.data.value?.title ?? `Project #${props.project.project_id}`);
const status = computed(() => (detail.data.value?.project_status ?? "").replace(/_/g, " ").replace(/^./, c => c.toUpperCase()));
const date = computed(() => new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(props.createdAt));
// Render the brief as text, including when the synchronized project stores HTML.
const brief = computed(() => (detail.data.value?.description ?? "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim());
</script>
<template>
  <div class="mx-auto w-full max-w-md py-4">
    <UiText as="p" variant="small" :text="`${project.actor_name} started a project`" class="mb-3 text-center text-se-gray-600" />
    <UiNavLink :to="`/${side}/projects/${project.project_id}`" class="group block overflow-hidden rounded-xl border border-se-gray-200 bg-white transition-colors hover:border-se-gray-400 focus-visible:outline-2 focus-visible:outline-se-dark">
      <div class="flex items-center justify-between border-b border-se-gray-200 bg-se-gray-100 px-5 py-3">
        <div class="flex items-center gap-2"><UiIcon name="file-placeholder" class="h-4 w-4" /><UiText as="span" variant="caption" text="PROJECT" /></div>
        <UiText as="span" variant="small" :text="status" class="text-se-gray-600" />
      </div>
      <div class="flex flex-col gap-2 p-5">
        <UiText as="h3" variant="h5" :text="title" />
        <UiText v-if="detail.isPending.value" as="p" variant="small" text="Loading project details…" class="text-se-gray-600" />
        <UiText v-else-if="detail.error.value" as="p" variant="small" text="Project details are unavailable. Open the project to check access." class="text-se-gray-600" />
        <UiText v-if="brief" as="p" variant="p-small" :text="brief" class="line-clamp-3 text-se-gray-600" />
        <div class="mt-3 flex items-center justify-between">
          <UiText as="span" variant="small" :text="`#${project.project_id} · ${date}`" class="text-se-gray-600" />
          <div class="flex items-center gap-2"><UiText as="span" variant="button" text="Open project" /><UiIcon name="arrow-right" class="h-4 w-4 transition-transform group-hover:translate-x-1" /></div>
        </div>
      </div>
    </UiNavLink>
  </div>
</template>
